/**
 * ConfidenceLevelSelector — the "how likely is it that this person is autistic?"
 * decision control shared by the Adult Autism SSI Review, Observation Review,
 * and MDT Review (side-by-side) pages.
 *
 * It supports two visual styles so it can match whatever the surrounding module
 * already uses:
 *
 *   • 'ternary'    — four labelled buttons (Very Unlikely → Very Likely). This
 *                    is the style used by the Adult Autism pathway.
 *   • 'percentage' — an 11-step 0–100% segmented bar with a red→amber→green
 *                    gradient. Retained so the control is a drop-in replacement
 *                    anywhere the older percentage style is configured.
 *
 * The panel uses the same amber "not in report" treatment as Emerging
 * Differentials / Emerging Formulation / Confidential Notes: amber border and
 * background, plus a low-emphasis "Not included in final report" caption in
 * the bottom-right. Confidence is a clinician working judgement for the
 * appointment, not a report paragraph input.
 *
 * This is a fully CONTROLLED component: it holds no state of its own. The parent
 * owns both the percentage value (`confidence`) and the ternary value
 * (`confidenceTernary`) and is notified of changes via the two callbacks. Only
 * one of the two values is relevant at a time depending on `confidenceStyle`,
 * but we accept both so a parent can switch styles without losing data.
 *
 * Read-only behaviour: when `canEdit` is false the buttons are disabled and
 * clicks are ignored, so the same component renders correctly in preview mode.
 */

import React from 'react';

/** The 11 fixed steps (0,10,…,100) rendered by the percentage segmented bar. */
const CONFIDENCE_STEPS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

/**
 * The four ternary options, in increasing-likelihood order. Each carries the
 * Tailwind classes for its "active" (selected) appearance and its hover border
 * so the colour ramps red → orange → lime → emerald.
 */
const TERNARY_OPTIONS = [
  { value: 'very_unlikely', label: 'Very Unlikely', activeBg: 'bg-red-100 border-red-400 text-red-700',           hoverBorder: 'hover:border-red-300' },
  { value: 'unlikely',      label: 'Unlikely',      activeBg: 'bg-orange-100 border-orange-400 text-orange-700',  hoverBorder: 'hover:border-orange-300' },
  { value: 'likely',        label: 'Likely',        activeBg: 'bg-lime-100 border-lime-400 text-lime-700',        hoverBorder: 'hover:border-lime-300' },
  { value: 'very_likely',   label: 'Very Likely',   activeBg: 'bg-emerald-100 border-emerald-400 text-emerald-700', hoverBorder: 'hover:border-emerald-300' },
];

export default function ConfidenceLevelSelector({
  confidenceStyle = 'ternary',
  confidence = null,
  confidenceTernary = null,
  onConfidenceChange,
  onConfidenceTernaryChange,
  canEdit = true,
  description = 'How likely is it in your clinical opinion that this person is autistic?',
  className = '',
}) {
  /**
   * Clicking an already-selected option clears it (toggle to null); clicking a
   * different option selects it. We short-circuit entirely when the page is
   * read-only so preview viewers cannot mutate the decision.
   */
  const handleTernaryClick = (value) => {
    if (!canEdit) return;
    onConfidenceTernaryChange?.(confidenceTernary === value ? null : value);
  };

  const handlePercentageClick = (step) => {
    if (!canEdit) return;
    onConfidenceChange?.(confidence === step ? null : step);
  };

  return (
    // Amber shell matches NotInReportNotesPanel / Emerging Differentials so
    // clinicians can see at a glance that this judgement stays internal.
    <div className={`rounded-2xl border border-amber-200 bg-amber-50 px-5 pt-5 pb-2 space-y-1 ${className}`}>
      <div className="pt-1">
        <h3 className="text-sm font-bold text-gray-800 mb-0.5">
          Confidence Level<span className="text-red-500 ml-0.5">*</span>
        </h3>
        <p className="text-xs text-gray-500 mb-2">{description}</p>

        {confidenceStyle === 'percentage' ? (
          <div className="flex items-center gap-0.5 mb-2">
            {CONFIDENCE_STEPS.map((step) => {
              const isSelected = confidence !== null && step <= confidence;
              const isExact = step === confidence;
              let selectedColour = 'bg-red-400';
              if (step >= 40 && step < 70) selectedColour = 'bg-amber-400';
              if (step >= 70) selectedColour = 'bg-emerald-400';

              return (
                <button
                  key={step}
                  type="button"
                  disabled={!canEdit}
                  onClick={() => handlePercentageClick(step)}
                  className={`
                    flex-1 h-7 first:rounded-l-md last:rounded-r-md
                    font-bold transition-all duration-200
                    ${isSelected
                      ? `${selectedColour} text-white`
                      : 'bg-gray-200 text-gray-400 hover:bg-gray-300'}
                    ${isExact ? 'ring-2 ring-offset-1 ring-primary z-10 relative text-xs' : 'text-[10px]'}
                    ${!canEdit ? 'cursor-default' : ''}
                  `}
                  title={`${step}%`}
                >
                  {step}%
                </button>
              );
            })}
          </div>
        ) : (
          <div className="flex items-center gap-2 mb-2">
            {TERNARY_OPTIONS.map((opt) => {
              const isActive = confidenceTernary === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  disabled={!canEdit}
                  onClick={() => handleTernaryClick(opt.value)}
                  className={`
                    flex-1 py-2.5 rounded-lg border-2 text-sm font-semibold transition-all duration-200
                    ${isActive
                      ? opt.activeBg
                      : `bg-white border-gray-300 text-gray-500 ${opt.hoverBorder} hover:text-gray-700`}
                    ${!canEdit ? 'cursor-default' : ''}
                  `}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {/* Low-emphasis caption mirroring NotInReportNotesPanel: bottom-right,
          italicised — not a badge. */}
      <p className="text-xs italic text-amber-900/80 text-right mt-1 mb-0">
        Not included in final report
      </p>
    </div>
  );
}

export { CONFIDENCE_STEPS, TERNARY_OPTIONS };
